import React, { useState } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import LogoutModal from '../Layout/LogoutModal';
import useTheme from '../../hooks/useTheme';
import { avatarColor, AvatarIcon } from '../../utils/avatar';

/* ── SVG icons ── */
const IcHome = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
    <polyline points="9 22 9 12 15 12 15 22"/>
  </svg>
);
const IcVideo = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="23 7 16 12 23 17 23 7"/>
    <rect x="1" y="5" width="15" height="14" rx="2"/>
  </svg>
);
const IcEdit = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
  </svg>
);
const IcFile = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
    <polyline points="14 2 14 8 20 8"/>
  </svg>
);
const IcClipboard = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2"/>
    <rect x="9" y="3" width="6" height="4" rx="2"/>
    <line x1="9" y1="12" x2="15" y2="12"/>
    <line x1="9" y1="16" x2="12" y2="16"/>
  </svg>
);
const IcChart = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="20" x2="18" y2="10"/>
    <line x1="12" y1="20" x2="12" y2="4"/>
    <line x1="6" y1="20" x2="6" y2="14"/>
    <line x1="2" y1="20" x2="22" y2="20"/>
  </svg>
);
const IcUser = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
    <circle cx="12" cy="7" r="4"/>
  </svg>
);
const IcLogout = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
    <polyline points="16 17 21 12 16 7"/>
    <line x1="21" y1="12" x2="9" y2="12"/>
  </svg>
);
const IcMoon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
  </svg>
);
const IcMenu = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/>
  </svg>
);
const IcSun = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/>
    <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/>
    <line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/>
    <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/>
  </svg>
);

const NAV_ITEMS = [
  { to: '/home',      Icon: IcHome,      label: 'Главная' },
  { to: '/lessons',   Icon: IcVideo,     label: 'Вебинары' },
  { to: '/homework',  Icon: IcEdit,      label: 'Домашние задания' },
  { to: '/tests',     Icon: IcClipboard, label: 'Варианты' },
  { to: '/materials', Icon: IcFile,      label: 'Материалы' },
  { to: '/progress',  Icon: IcChart,     label: 'Прогресс' },
  { to: '/profile',   Icon: IcUser,      label: 'Профиль' },
];

const StudentSidebar = ({ courses = [], selectedCourseId, isOpen }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [logoutOpen, setLogoutOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem('s_sidebar_collapsed') === '1');
  const { dark, toggle } = useTheme();

  const selectedCourse = courses.find(c => c.id === selectedCourseId) || null;

  const toggleCollapse = () => setCollapsed(c => {
    localStorage.setItem('s_sidebar_collapsed', !c ? '1' : '0');
    return !c;
  });

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const ASSET = `${process.env.PUBLIC_URL || ''}/landing`;

  return (
    <div className={`student-sidebar${isOpen ? ' sidebar--open' : ''}${collapsed ? ' collapsed' : ''}`}>
      {/* Декоративные слои: текстура кода + свечение */}
      <div className="sb-code" style={{ backgroundImage: `url(${ASSET}/pattern-brand.png)` }} />
      <div className="sb-glow" />

      {/* Brand */}
      <div className="s-brand">
        <div className="s-brand-top">
          <div className="s-logo-box">
            <img className="sb-logo-img" src={`${ASSET}/logo-hq.png`} alt="ИНФА СОТКА" />
            <img className="sb-logo-mark" src={`${ASSET}/logo-mark.png`} alt="" aria-hidden="true" />
          </div>
          {/* кнопка «свернуть» — рядом с лого, видна только когда сайдбар открыт */}
          <button className="s-collapse-btn" onClick={toggleCollapse} aria-label="Свернуть меню" title="Свернуть меню">
            <IcMenu />
          </button>
        </div>
        {/* Ряд 2 постоянной высоты: текст при открытом (плавно появляется),
            кнопка «развернуть» при закрытом — чтобы разделы не прыгали. */}
        <div className="s-brand-row2">
          {collapsed ? (
            <button className="s-open-btn" onClick={toggleCollapse} aria-label="Развернуть меню" title="Развернуть меню">
              <IcMenu />
            </button>
          ) : (
            <div className="s-brand-sub">Платформа обучения</div>
          )}
        </div>
      </div>

      {/* User — клик ведёт в профиль */}
      <div className="s-user s-user--link" onClick={() => navigate('/profile')} title="Открыть профиль">
        <div className="s-avatar" style={user?.avatar ? undefined : { background: avatarColor(user?.id) }}>
          {user?.avatar
            ? <img src={user.avatar} alt="" />
            : <AvatarIcon id={user?.id} size={22} />}
        </div>
        <div className="s-user-info">
          <h4>{user?.name || 'Ученик'}</h4>
          <p>{selectedCourse?.name || 'Нет курса'}</p>
        </div>
      </div>

      {/* Nav */}
      <ul className="s-nav">
        {NAV_ITEMS.map((item) => {
          // «Варианты» подсвечиваем и на экране решения варианта (/variant/:id)
          const forceActive = item.to === '/tests' && location.pathname.startsWith('/variant');
          return (
            <li key={item.to} className="s-nav-item">
              <NavLink
                to={item.to}
                title={item.label}
                className={({ isActive }) => `s-nav-link ${(isActive || forceActive) ? 'active' : ''}`}
              >
                <span className="s-nav-icon"><item.Icon /></span>
                <span>{item.label}</span>
              </NavLink>
            </li>
          );
        })}
      </ul>

      {/* Footer */}
      <div className="s-sidebar-footer">
        <button className="sb-theme" onClick={toggle} title={dark ? 'Светлая тема' : 'Тёмная тема'}>
          <span className="s-nav-icon">{dark ? <IcSun /> : <IcMoon />}</span>
          <span>{dark ? 'Светлая тема' : 'Тёмная тема'}</span>
        </button>
        <button className="s-logout-btn" onClick={() => setLogoutOpen(true)} title="Выйти">
          <span className="s-nav-icon"><IcLogout /></span>
          <span>Выйти</span>
        </button>
      </div>

      <LogoutModal open={logoutOpen} onClose={() => setLogoutOpen(false)} onConfirm={handleLogout} />
    </div>
  );
};

export default StudentSidebar;
